/* eslint-disable eslint-comments/disable-enable-pair */
/* eslint-disable @typescript-eslint/no-unnecessary-condition */
/* eslint-disable i18next/no-literal-string */
// TODO: Add types for mixpanel and IA: https://lastbrand.atlassian.net/browse/SFPLTFRM-873
import { IS_PROD_ENV, IS_PROD_BUILD } from '@src/config/env'
import { getIsCountryCompliant } from '@src/modules/global-state/locale-config.browser'
import CUSTOM_SECTION_CONTAINER_TYPES from '@src/quince-site-common/src/constants/customSectionContainerTypes'
import { ProductVariantOption } from '@src/quince-site-common/src/domains/ProductVariant'
import { CustomAttribute } from '@src/quince-site-common/src/domains/sp/CustomAttribute'
import { getSvid } from '@src/quince-site-common/src/hooks/cart/hooks/new-cart-helper'
import { computeScrollPercentage } from '@src/quince-site-common/src/hooks/useScrollEventTracker'
import { getZipCodeFromCookie } from '@src/quince-site-common/src/hooks/zipcode/utils/zipcode.utils'
import AuthUtil from '@src/quince-site-common/src/utils/authUtil'
import { ANALYTIC_COOKIES, USER_CONSENT_COOKIES } from '@src/quince-site-common/src/utils/constants'
import { getAppAnalyticsData } from '@src/quince-site-common/src/utils/cross-platform-analytics/cross-platform-analytics.utils'
import { decodeSpidFromGraphqlIdResponse } from '@src/quince-site-common/src/utils/decodeSpidFromGraphqlIdResponse'
import getUtmParameters from '@src/quince-site-common/src/utils/get-utm-parameters'
import getCookie from '@src/quince-site-common/src/utils/getCookie'
import { getOSDetails, getUserBrowser } from '@src/quince-site-common/src/utils/getDeviceDetailsFromUserAgent'
import { getlbUserId } from '@src/quince-site-common/src/utils/getlb-user-id'
import { getProductTagsDataForBatchedAnalyticsFromTags } from '@src/quince-site-common/src/utils/getProductTagsDataForBatchedAnalytics'
import isBrowser from '@src/quince-site-common/src/utils/isBrowser'
import { ProductProperties } from '@src/telemetry/getProductPropertiesForTracking'
import { TELEMETRY_EVENT_NAMES } from '@src/telemetry/telemetry-events.constants'
import { transformTagsIntoAnalyticsTags } from '@src/utils/general.utils'
import { getSessionId } from '@src/utils/user/session.browser'

interface RecommendationContext {
  rec_module_id?: string | number
  rec_element_name?: string
  rec_product_count?: number
  is_cart_modal?: boolean
  product_position?: { i: number; j: number }
}

export const getRecommendationProperties = (context?: Partial<RecommendationContext>) => {
  if (!context) return {}

  const properties: Partial<RecommendationContext> = {}
  if (context.rec_module_id) {
    properties.rec_module_id = context.rec_module_id
  }

  if (context.rec_element_name) {
    properties.rec_element_name = context.rec_element_name
  }

  if (context.rec_product_count) {
    properties.rec_product_count = context.rec_product_count
  }

  if (typeof context.is_cart_modal === 'boolean') {
    properties.is_cart_modal = context.is_cart_modal
  }

  if (context.product_position && typeof context.product_position === 'object') {
    properties.product_position = context.product_position
  }

  return properties
}

export const getIsProd = () => isBrowser() && IS_PROD_ENV && IS_PROD_BUILD

export const getCommonProperties = (properties: any) => {
  const url = new URL(properties.url)
  const urlParamObj: Record<string, string> = {}
  url.searchParams.forEach((val: string, key: string) => {
    urlParamObj[key] = val
  })
  const browserDetails = getUserBrowser()
  const osDetails = getOSDetails()
  const eventTimestamp = new Date(properties.event_time).getTime()
  const { context: app_context_data, ...appRestEventData } = getAppAnalyticsData()

  const customerIdFromStorage =
    getCookie(ANALYTIC_COOKIES.LB_CUSTOMER_ID) ??
    // eslint-disable-next-line @quince/no-direct-localstorage
    localStorage.getItem(ANALYTIC_COOKIES.LB_CUSTOMER_ID) ??
    ''
  const mixpanelDeviceId = getCookie(ANALYTIC_COOKIES.MIXPANEL_USER_DEVICE_ID) ?? ''
  const isLoggedIn = AuthUtil.isLoggedIn()

  // $user_id identity resolution.
  //
  // countryCompliant locales (UK / GDPR): strict opt-in. The real customer
  // identity may only be stitched once the user has explicitly granted
  // advertising consent via TrustArc. Until then $user_id is null so that
  // pre-consent (and no-consent) SGTM events stay pseudonymous and cannot be
  // linked to the logged-in customer. Device-id rotation on consent grant
  // (mixpanelCl.rotateDeviceId) keys off the same advertising-consent signal,
  // so gating $user_id on it keeps the two consistent.
  //   - no consent (pre- or post-login)        → null
  //   - consent granted, not logged in          → null
  //   - consent granted, logged in              → customerId
  //
  // Non-compliant locales (US / CA): unchanged opt-out behaviour —
  // customerId when logged in, device id otherwise.
  const isCountryCompliant = getIsCountryCompliant()
  const hasAdvertisingConsent = getCookie(USER_CONSENT_COOKIES.ADVERTISING) === 'true'

  const $user_id = isCountryCompliant
    ? hasAdvertisingConsent && isLoggedIn
      ? customerIdFromStorage
      : null
    : isLoggedIn
      ? customerIdFromStorage
      : mixpanelDeviceId

  // lb_user_id is a persistent pseudonymous identifier. For countryCompliant (UK)
  // locales it must NOT enter SGTM events until advertising consent is granted —
  // null before consent and after revoke, populated only once consent === true.
  // US/CA: always sent (unchanged). Matches the consent-state table alongside
  // $user_id / $device_id / session_id (which rotate on consent grant + revoke).
  const lbUserId = isCountryCompliant ? (hasAdvertisingConsent ? getlbUserId() : null) : getlbUserId()

  const commonProperties = {
    ...appRestEventData,
    country: properties?.country,
    currency: properties?.currency,
    language: properties?.language,
    locale: properties?.locale,
    browser_language: properties?.browser_language,
    region_id: properties?.region_id,
    app_context_data,
    event_id: properties.id,
    event_timestamp: eventTimestamp,
    active_experiment: properties.active_experiments,
    page_url: properties.url,
    visit_url: properties.url,
    visit_page: url.pathname,
    visit_url_params: urlParamObj,
    device_type: properties.device,
    session_id:
      properties.session_id ||
      getSessionId() ||
      // eslint-disable-next-line @quince/no-direct-localstorage
      localStorage.getItem(ANALYTIC_COOKIES.SESSION_ID),
    is_logged_in: isLoggedIn,
    $device_id: mixpanelDeviceId || undefined,
    $user_id,
    lb_user_id: lbUserId,
    page_name: properties?.page_name ?? properties?.context?.page_name,
    page_type: properties?.page_type,
    page_path: properties?.page_path ?? properties?.context?.page_path,
    ...(properties?.page_variant && { page_variant: properties.page_variant }),
    previous_page_name: properties?.previous_page_name,
    previous_page_type: properties?.previous_page_type,
    previous_page_path: properties?.previous_page_path ?? properties?.context?.previous_page_path,
    ...(properties?.previous_page_variant && { previous_page_variant: properties.previous_page_variant }),
    is_in_first_fold: computeScrollPercentage() === 0 ? 1 : 0,
    zip_code: getZipCodeFromCookie(false) ?? undefined,
    is_prod: getIsProd(),
    customer_id:
      getCookie(ANALYTIC_COOKIES.LB_CUSTOMER_ID) ??
      // eslint-disable-next-line @quince/no-direct-localstorage
      localStorage.getItem(ANALYTIC_COOKIES.LB_CUSTOMER_ID),
    storefront_id: properties.context?.storefront_id,
  }
  const { utm_source, utm_medium, utm_campaign, utm_content } = getUtmParameters()
  return {
    sgtmCommonProperties: {
      ...commonProperties,
      ip: getCookie(ANALYTIC_COOKIES.ECOMM_IP),
      $os: properties.device_category,
      $os_version: osDetails.osVersion,
      $browser: browserDetails.browserName,
      $browser_version: browserDetails.browserVersion,
      time: eventTimestamp,
      $insert_id: properties.id,
      utm_source,
      utm_medium,
      utm_campaign,
      utm_content,
    },

    clientCommonProperties: {
      ...commonProperties,
    },
  }
}

export const getB2BProperties = (isB2BFlow: boolean, isCompanyFunded: boolean) => {
  if (!isB2BFlow) return {}
  return { is_company_funded: isCompanyFunded }
}

const PDP_ABOVE_FOLD_EVENT_NAMES: Record<string, string> = {
  product_name: 'Product Name Viewed',
  product_rating: 'Product Rating Viewed',
  product_price: 'Product Price Viewed',
  color_variant_section: 'Product Color Variant Section Viewed',
  size_variant_section: 'Product Size Variant Section Viewed',
  add_to_cart: 'Add To Cart Button Viewed',
  earn_credit: 'Earn Quince Credit Viewed',
  price_comparison_major_marketplaces: 'Major Marketplace Price Comparison Viewed',
}

export const getTransformedProperties = (properties: Record<string, unknown>): Record<string, unknown> | null => {
  switch (properties.event_type) {
    case 'page_view':
      return {
        event_type: 'page_load',
        event_name: 'Page Viewed',
        landing_page: properties?.landing_page ?? properties?.context?.landing_page,
      }

    case 'home_page_view':
      return {
        event_type: 'page_load',
        event_name: 'Home Page Viewed',
      }

    case 'wishlist_page_view':
      return {
        event_type: 'page_load',
        event_name: 'Wishlist Page Viewed',
      }

    case 'search_page_view':
      return {
        event_type: 'page_load',
        event_name: 'Search Page Viewed',
      }

    case 'collection_view':
      return {
        event_type: 'page_load',
        event_name: properties.context?.isDynamicCollectionPage
          ? 'Dynamic Collection Page Viewed'
          : 'Collection Page Viewed',
      }

    case 'product_view':
      return {
        event_type: 'page_load',
        event_name: 'PDP Viewed',
        product_id: properties.context?.product_id,
        name: properties.context?.name,
        products: properties.context?.name ? [properties.context.name] : [],
        division: properties.context?.division,
        subdepartment: properties.context?.subDepartment,
        department: properties.context?.department,
        gender: properties.context?.gender,
        findingMethodAggregate: properties?.context?.findingMethodAggregate,
        findingMethodGranular: properties?.context?.findingMethodGranular,
        findingMethodId: properties?.context?.findingMethodId,
        is_final_sale: properties?.context?.is_final_sale,
        is_app_exclusive: properties?.context?.is_app_exclusive,
        has_video: properties?.context?.has_video,
        ...properties?.context?.productProperties,
      }

    case 'product_gallery_loaded':
      return {
        event_type: 'page_load',
        event_name: 'Product Gallery Loaded',
        product_id: properties.context?.product_id,
        name: properties.context?.name,
        division: properties.context?.division,
        subdepartment: properties.context?.subDepartment,
        department: properties.context?.department,
        gender: properties.context?.gender,
        findingMethodAggregate: properties?.context?.findingMethodAggregate,
        findingMethodGranular: properties?.context?.findingMethodGranular,
        findingMethodId: properties?.context?.findingMethodId,
        is_final_sale: properties?.context?.is_final_sale,
        is_app_exclusive: properties?.context?.is_app_exclusive,
        ...properties?.context?.productProperties,
      }

    case 'is_visible': {
      if (properties.entity_id && PDP_ABOVE_FOLD_EVENT_NAMES[properties.entity_id as string]) {
        return {
          event_type: 'impression',
          event_name: PDP_ABOVE_FOLD_EVENT_NAMES[properties.entity_id as string],
          element_name: properties.entity_id,
          product_id: properties.context?.product_id,
          name: properties.context?.name,
          division: properties.context?.division,
          subdepartment: properties.context?.subDepartment,
          department: properties.context?.department,
          gender: properties.context?.gender,
          findingMethodAggregate: properties?.context?.findingMethodAggregate,
          findingMethodGranular: properties?.context?.findingMethodGranular,
          findingMethodId: properties?.context?.findingMethodId,
          is_final_sale: properties?.context?.is_final_sale,
          is_app_exclusive: properties?.context?.is_app_exclusive,
          ...properties?.context?.productProperties,
        }
      }

      if (properties.entity_id === 'klarna_payments') {
        return {
          event_type: 'impression',
          event_name: 'Klarna Payments Message Viewed',
          element_name: 'klarna_payments',
          element_display_name: 'klarna_payments',
        }
      }

      const element_name =
        properties.context?.contentContainerName ||
        properties.entity_id ||
        properties.context?.childToParentTrackingIds?.[0]

      let event_name = 'impression'
      if (properties.entity_type === 'product_card_view') {
        event_name = 'Product List Viewed'
      } else if (properties.context?.contentContainerType === CUSTOM_SECTION_CONTAINER_TYPES.CATEGORY_DISPLAY) {
        if (!properties.context?.category_name) return
        event_name = `${properties.context.contentContainerType} Viewed`
      } else if (properties.context?.contentContainerType) {
        event_name = `${properties.context.contentContainerType} Viewed`
      } else if (properties?.entity_id === 'app_exclusive_message') {
        event_name = 'App Exclusive Message Viewed'
      } else if (properties?.entity_id === 'write_review_section_viewed') {
        event_name = 'Write Reviews Section Viewed'
      }
      if (properties.entity_id === 'quince_credit') {
        event_name = 'Earn Quince Credit Viewed'
      } else if (properties.entity_id === 'best_market_value') {
        event_name = 'Best Market Value Viewed'
      }

      if (properties.entity_id === 'pdp_reviews' || properties.entity_id === 'pdp_accordion_reviews') {
        event_name = 'Review List Viewed'
      } else if (properties.entity_id === 'pdp_accordion_reviews_summary') {
        event_name = 'Review Summary Viewed'
      } else if (properties.entity_id === 'pdp_reviews_gallery') {
        event_name = 'Review Gallery Viewed'
      } else if (properties.entity_id === 'pdp_review_tags') {
        event_name = 'Review Tags Displayed'
      }

      let modifiedProperties: any = {
        event_type: 'impression',
        event_name: event_name,
        element_name: element_name,
        element_id: properties.context?.trackingId || properties.context?.id,
        element_display_name:
          properties.context?.contentContainerDisplayName ||
          properties.context?.displayTitle ||
          properties.context?.childToParentTrackingIds?.[0] ||
          properties.entity_id,
        element_type: properties.context?.contentContainerType,
        category_name: properties.context?.category_name,
        category_position: properties.context?.category_position,
        tabData: getRecommendationProperties(properties?.context?.tabData),
        element_position:
          properties.context?.contentContainerPosition !== undefined
            ? properties.context.contentContainerPosition
            : properties.context?.elementPosition,
        reco_module: properties.context?.reco_module,
        is_final_sale: properties.context?.is_final_sale,
        ...properties?.context?.productProperties,
      }

      if (properties.entity_type === 'product_card_view') {
        modifiedProperties.product_count = properties.context?.products_list?.length || 0
        modifiedProperties.product_id = (properties.context?.products_list || []).map((product: { spid: number }) =>
          product.spid.toString(),
        )
        modifiedProperties.name = (properties.context?.products_list || []).map(
          (product: { name: string }) => product.name,
        )
        modifiedProperties.productProperties = (properties.context?.products_list || []).map(
          (product: {
            color: string
            product_id: number
            product_options: { name: string; color: string }[]
            tags: string[]
            oos_flag: boolean
            name: string
            color_variant: string[]
            count_color_variant: number
            rating: number | string
            wishlishted: boolean
            is_new: boolean
            back_in_stock: boolean
            sold_out: boolean
            low_stock: boolean
            best_seller: boolean
            spid: number
            productProperties: ProductProperties
          }) => ({
            ...product,
            wishlishted: product.wishlishted !== undefined && (product.wishlishted ? 1 : 0),
          }),
        )

        modifiedProperties = {
          ...modifiedProperties,
          ...getProductTagsDataForBatchedAnalyticsFromTags(properties?.context?.products_list || []),
          ...getRecommendationProperties(properties.context?.tabData),
        }
      }

      return modifiedProperties
    }

    case 'product_click': {
      const element_type = properties.context?.contentContainerType
      let event_name =
        element_type && properties.event_type !== 'product_click' ? `${element_type} Clicked` : 'Product Card Clicked'
      const element_name =
        properties.context?.contentContainerName || properties.context?.sectionType || properties.entity_id
      const element_display_name =
        properties.context?.contentContainerDisplayName || properties.context?.tracker_section

      if (
        properties?.entity_id === 'click_complete_the_look_stylitics' ||
        properties?.entity_id === 'click_complete_the_look_stylitics_modal'
      ) {
        event_name = 'Product Card Clicked'
      }
      return {
        ...properties?.context?.productProperties,
        event_type: 'click',
        event_name: event_name,
        element_name: element_name,
        element_display_name: element_display_name,
        element_type: element_type,
        element_id: properties.context?.trackingId || properties.context?.id || properties.context?.element_id,
        element_position:
          properties.context?.contentContainerPosition !== undefined
            ? properties.context.contentContainerPosition
            : properties.context?.elementPosition,
        product_id: properties.context?.product_id,
        color: properties.context?.product_color || properties.context?.color,
        color_swatch_position: properties.context?.color_swatch_position,
        name: properties.context?.name,
        color_variant: properties.context?.color_variant,
        count_color_variant: properties.context?.count_color_variant,
        rating: properties.context?.rating,
        product_position:
          properties.context?.product_position && typeof properties.context.product_position === 'object'
            ? properties.context.product_position
            : properties.context?.product_position
              ? Number(properties.context.product_position) + 1
              : 0,
        tags: properties.context?.tags,
        tag_ids: transformTagsIntoAnalyticsTags(properties.context?.tags ?? []),
        is_new: properties.context?.is_new,
        back_in_stock: properties.context?.back_in_stock,
        sold_out: properties.context?.sold_out,
        low_stock: properties.context?.low_stock,
        best_seller: properties.context?.best_seller,
        tabData: getRecommendationProperties(properties?.context?.tabData),
        reco_module: properties.context?.reco_module,
        ...properties?.content?.productProperties,
        is_final_sale: properties.context?.is_final_sale,
        is_app_exclusive: properties.context?.is_app_exclusive,
      }
    }

    case 'button_click': {
      const element_name =
        properties.context?.contentContainerName ||
        properties.context?.childToParentTrackingIds?.reverse().join('__') ||
        properties.context?.label ||
        properties.context?.element_name ||
        'Button'
      const lineItems = properties.entity_id === 'start_checkout' ? properties.context?.lineItems || [] : []
      const productIdsSet = new Set(
        lineItems.map(li => li.customAttributes?.find((ca: CustomAttribute) => ca.key === '_productId')?.value),
      )
      const productIdsArr = Array.from(productIdsSet)
      const formattedLineItems = lineItems.map(li => {
        const svid = getSvid(li)
        return {
          sku: li.variant?.sku,
          name: li.title,
          price: li.variant?.priceV2?.amount ? Number(li.variant.priceV2.amount) : undefined,
          color: li.variant?.selectedOptions?.find((so: ProductVariantOption) => so.name === 'color')?.value,
          size: li.variant?.selectedOptions?.find((so: ProductVariantOption) => so.name === 'Size')?.value,
          variant_id: svid ? decodeSpidFromGraphqlIdResponse(svid) : undefined,
        }
      })
      let event_name = `${properties.context?.contentContainerType || element_name} Clicked`
      if (properties.entity_id === 'start_checkout') {
        event_name = 'Checkout Initiated'
      } else if (properties.entity_id === 'click_menu_link') {
        event_name = 'Top Nav Clicked'
      } else if (properties.entity_id == 'click_quick_shop') {
        event_name = 'Product Popup Viewed'
      } else if (element_name == 'menu_display_state') {
        event_name = 'Mobile Menu Opened'
      } else if (properties?.context?.element_name == 'Product Display') {
        event_name = 'Product Display Clicked'
      } else if (properties.entity_id === 'header_mega_menu_toggle') {
        event_name = 'Mobile Menu Navigation'
      } else if (properties.entity_id === 'attentive_email_submitted') {
        event_name = 'Signup Attributes Added'
      } else if (properties?.entity_id === 'click_next_reviews_page') {
        event_name = 'View more reviews clicked'
      } else if (properties.entity_id === 'return_initiated') {
        event_name = 'Return Initiated'
      } else if (properties.entity_id === 'return_successful') {
        event_name = 'Return Successful'
      } else if (properties?.entity_id === 'search_button_click') {
        event_name = 'Search Clicked'
      } else if (properties?.entity_id === 'easy_returns') {
        event_name = 'Easy Returns Clicked'
      } else if (properties?.entity_id === 'shipping_delayed_contact_clicked') {
        event_name = 'Shipping Delayed Contact Clicked'
      } else if (properties?.entity_id === 'write_review_click') {
        event_name = 'Write Review Clicked'
      } else if (properties?.entity_id === 'click_complete_the_look_stylitics') {
        event_name = 'CTL Stylitics Modal Viewed'
      } else if (properties?.entity_id === 'shop_in_app') {
        event_name = 'Shop In The App Clicked'
      } else if (
        (properties?.entity_id === 'click__carousel__right' || properties?.entity_id === 'click__carousel__left') &&
        properties.context?.contentContainerType === 'Reviews Gallery'
      ) {
        event_name = 'Review Gallery Scrolled'
      } else if (properties?.entity_id === 'click_reviews_gallery') {
        event_name = 'Review Gallery Clicked'
      }
      return {
        event_type: properties.entity_id === 'start_checkout' ? 'checkout' : 'click',
        event_name: event_name,
        nav_element:
          properties.entity_id === 'click_menu_link' ? properties.context?.menuTitle : properties.context?.nav_element,
        top_nav_element: properties.entity_id === 'click_menu_link' ? properties.context?.menuTitle : undefined,
        element_name: element_name,
        product_id: properties.context?.product_id ? properties.context.product_id.toString() : undefined,
        price: properties.context?.price
          ? Number(properties.context.price)
          : properties.context?.product_price
            ? Number(properties.context.product_price)
            : undefined,
        element_type: properties.context?.contentContainerType,
        element_display_name:
          properties.context?.contentContainerDisplayName || properties.context?.element_display_name,
        element_id: properties.context?.trackingId || properties.context?.id || properties.context?.element_id,
        product_price: properties.context?.product_price,
        product_count: productIdsArr.length,
        cart_value: properties.context?.isCompanyFunded
          ? undefined
          : properties.context?.totalPrice
            ? Number(properties.context.totalPrice)
            : undefined,
        variants: formattedLineItems,
        name: properties.context?.name,
        color: properties.context?.color_name,
        color_variant: properties.context?.color_variant,
        rating: properties.context?.rating,
        custom_variants: properties.context?.custom_variants,
        oos_flag: properties.context?.oos_flag,
        gender: properties.context?.gender,
        creativeId: properties.context?.creativeId,
        tabData: getRecommendationProperties(properties?.context?.tabData),
        ...properties?.context.productProperties,
        L1: properties?.context?.L1,
        L2: properties?.context?.L2,
        L3: properties?.context?.L3,
        ...getB2BProperties(properties.context?.isB2BFlow, properties.context?.isCompanyFunded),
      }
    }

    case 'link_click': {
      if (properties.entity_id === 'klarna_payments') {
        return {
          event_type: 'click',
          event_name: 'Klarna Payments Link Clicked',
          element_name: 'klarna_payments',
          element_display_name: 'klarna_payments',
        }
      }

      const element_display_name =
        properties.context?.contentContainerDisplayName ||
        properties.context?.displayTitle ||
        properties.context?.sectionType

      let event_name = 'Link Clicked'
      if (properties?.context?.contentContainerType) {
        event_name = `${properties?.context?.contentContainerType} Clicked`
      } else if (properties?.entity_id === 'account_link_click') {
        event_name = 'Profile Clicked'
      } else if (properties?.entity_id === 'wishlist_link_click') {
        event_name = 'Wishlist Clicked'
      }

      return {
        event_type: 'click',
        event_name: event_name,
        category_name: properties.context?.label !== undefined ? [properties.context.label] : undefined,
        category_position:
          properties.context?.col !== undefined && properties.context.row !== undefined
            ? [Number(properties.context.col) * 2 + (Number(properties.context.row) + 1)]
            : undefined,
        element_display_name: element_display_name,
        element_name: properties.context?.contentContainerName,
        element_id: properties.context?.trackingId || properties.context?.id || properties.context?.element_id,
        element_type: properties.context?.contentContainerType,
        element_position:
          properties.context?.contentContainerPosition !== undefined
            ? properties.context.contentContainerPosition
            : properties.context?.elementPosition,
        horizontal_position: properties?.context?.horizontalPosition,
      }
    }

    case 'klaviyo_newsletter_subscribe':
      return {
        event_type: 'email_subscription',
        event_name: 'Email Subscribed',
        subscription_succesful: 1,
      }

    case 'page_scroll':
      return {
        event_type: 'page_scroll',
        event_name: 'Page Scrolled',
        scroll_percentage: properties.entity_id,
        is_modal: properties.context?.is_modal ?? false,
      }

    case 'filter_click':
      return {
        event_type: 'click',
        event_name: 'Dropdown filter used',
        filter_used: properties.context?.label,
        filter_value: properties.context?.value,
      }

    case 'view_bag_button_clicked':
      return {
        event_type: 'click',
        event_name: 'Clicked View Bag Button',
        is_modal: properties.context?.is_modal,
      }

    case 'checkout_button_clicked':
      return {
        event_type: 'click',
        event_name: 'Clicked Checkout Button',
        is_modal: properties.context?.is_modal,
      }

    case 'buy_now':
      return {
        event_type: 'click',
        event_name: 'Buy Now Clicked',
        cart_id: (properties.context as { cartID?: string })?.cartID,
        variants: properties?.context?.line_items,
        ...properties?.context?.productProperties,
      }

    case 'apple_pay_clicked':
      return {
        event_type: 'click',
        event_name: 'Apple Pay Clicked',
        variants: properties?.lineItems ?? properties?.context?.line_items,
        ...(properties?.productProperties ?? properties?.context?.productProperties),
      }

    case 'variant_select':
      return {
        event_type: 'click',
        event_name: 'Product Option Selected',
        product_id: properties.context?.product_id ? properties.context.product_id.toString() : undefined,
        division: properties.context?.division,
        department: properties.context?.department,
        subdepartment: properties.context?.subdepartment,
        product_option_name: properties.context?.product_option_name,
        product_option_value: properties.context?.product_option_value,
        product_option_value_position: properties.context?.product_option_value_position,
        autoselect: !!properties.context?.autoselect,
        message_displayed: properties.context?.message_displayed,
        element_name: properties.context?.element_name || null,
        element_display_name: properties.context?.element_display_name || null,
        element_type: properties.context?.element_type || null,
        element_id: properties.context?.element_id || null,
        element_position: properties.context?.element_position || null,
        is_limited_edition_color: properties.context?.is_limited_edition_color,
        ...properties?.context?.productProperties,
      }

    case 'add_to_wishlist':
      return {
        event_type: 'click',
        event_name: 'Added to wishlist',
        product_id: properties.context?.product_id,
        element_name: properties.context?.tracker_section,
        color_name: properties.context?.color,
      }

    case 'remove_from_wishlist':
      return {
        event_type: 'click',
        event_name: 'Removed from wishlist',
        product_id: properties.context?.product_id,
        element_name: properties.context?.tracker_section,
        color_name: properties.context?.color,
        ...properties?.context?.productProperties,
      }

    case 'video_played':
      return {
        event_type: 'play',
        event_name: 'Video Played',
        element_name: properties.entity_id,
        variant_id: properties.context?.variant_id,
        video_id: properties.context?.video_id,
        video_format: properties.context?.video_format,
        video_position: properties.context?.video_position,
        autoplayed: properties.context?.autoplayed,
        ...properties?.context?.productProperties,
      }

    case 'video_completed':
      return {
        event_type: 'play',
        event_name: 'Video Completed',
        element_name: properties.entity_id,
        variant_id: properties.context?.variant_id,
        video_id: properties.context?.video_id,
        video_format: properties.context?.video_format,
        video_position: properties.context?.video_position,
        autoplayed: properties.context?.autoplayed,
        ...properties?.context?.productProperties,
      }

    case 'product_thumbnail_zoom':
      return {
        event_type: 'hover',
        event_name: 'Product Thumbnail Zoomed',
        product_id: properties.context?.product_id,
        ...properties?.context?.productProperties,
      }

    case 'join_the_waitlist': {
      return {
        event_type: 'click',
        event_name: 'Waitlist Joined',
        variant_id: properties?.entity_id,
        ...properties?.context?.productProperties,
      }
    }

    case 'add_to_cart': {
      const productProps = properties?.context?.productProperties as Partial<ProductProperties> | undefined
      const latestLineItem = properties.context?.line_items
        ? properties.context.line_items?.find(
            lineItem => lineItem?.variantId && lineItem?.variantId == properties.entity_id,
          )
        : undefined

      return {
        event_type: 'click',
        event_name: 'Added to Cart',
        cart_id: (properties.context as { cartID?: string })?.cartID,
        product_id: properties.context?.product_id ? properties.context.product_id.toString() : undefined,
        sku: latestLineItem?.sku,
        variant_id: latestLineItem?.variantId ? latestLineItem.variantId.toString() : undefined,
        name: latestLineItem?.title,
        price: latestLineItem?.price ? Number(latestLineItem?.price) : undefined,
        color: latestLineItem?.variantTitle.split(' / ')[1],
        size_variant: latestLineItem?.variantTitle.split(' / ')[0],
        products: [latestLineItem?.title],
        division: properties.context?.division,
        department: properties.context?.department,
        subdepartment: properties.context?.subDepartment,
        // We get cart_upsell_${trackerConfigKey} as trackerSection for mobile view quick shop modal.
        quick_add_to_cart:
          properties.context?.tracker_section === 'quick_shop' ||
          properties.context?.tracker_section === 'social_section' ||
          properties?.context?.tracker_section?.includes('cart_upsell_')
            ? 1
            : 0,
        color_variant: properties.context?.color_variant,
        gender: properties.context?.gender,
        tabData: getRecommendationProperties(properties?.context?.tabData),
        ...properties?.context.productProperties,
        findingMethodAggregate: properties?.context?.findingMethodAggregate,
        findingMethodGranular: properties?.context?.findingMethodGranular,
        findingMethodId: properties?.context?.findingMethodId,
        edd_standard_eligible: properties?.context?.edd_standard_eligible,
        edd_express_eligible: properties?.context?.edd_express_eligible,
        edd_label: properties?.context?.edd_label,
        edd_max_date: properties?.context?.edd_max_date,
        edd_min_date: properties?.context?.edd_min_date,
        location_ids: properties?.context?.location_ids,
        edd_max_days: properties?.context?.edd_max_days,
        edd_min_days: properties?.context?.edd_min_days,
        is_preorder: productProps?.is_preorder ?? properties?.context?.is_preorder,
        preorder_edd: productProps?.preorder_edd,
        color_swatch_position: properties?.context?.color_swatch_position,
        is_final_sale: properties?.context?.is_final_sale,
        is_app_exclusive: properties?.context?.is_app_exclusive,
        subscription_frequency: properties?.context?.subscription_frequency,
        variant_quantity_map: properties?.context?.variant_quantity_map,
        value: properties?.context?.value,
        is_customization_added: properties?.context?.is_customization_added,
        quantity: properties?.context?.quantity,
      }
    }
    case 'clicked_to_search':
      return {
        event_type: 'search',
        event_name: 'Search Initiated',
      }

    case 'search_triggered':
      return {
        event_type: 'search',
        event_name: 'Search Triggered',
        search_query: properties.context.search_query,
        query_length: properties.context.query_length,
        autocomplete_count: properties.context.autocomplete_count,
        autocomplete_selected: properties.context.autocomplete_selected,
        autocomplete_rank: properties.context.autocomplete_rank,
        autocomplete_term: properties.context.autocomplete_term,
        search_cancel: properties.context.search_cancel,
      }

    case 'top_nav_opened':
      return {
        event_type: 'menu_opened',
        event_name: 'Top Nav Opened',
        top_nav_element: properties.context.top_nav_element,
        label_visible: properties.context?.label_visible,
      }

    case 'profile_menu_opened':
      return {
        event_type: 'menu_opened',
        event_name: 'Profile Menu Opened',
      }

    case 'header_logo_clicked':
      return {
        event_type: 'click',
        event_name: 'Header Logo Clicked',
      }
    case 'app_push_banner_install_clicked':
      return {
        event_type: 'click',
        event_name: 'App Install Banner Clicked',
        bucket: properties.context?.bucket,
        locale: properties.context?.locale,
        universalLink: properties.context?.universalLink,
        deepLinkValue: properties.context?.deepLinkValue,
      }
    case 'app_install_clicked':
      return {
        event_type: 'click',
        event_name: 'App Install Link Clicked',
        link_source: properties.context?.link_source,
        page_type: properties.context?.page_type,
        page_name: properties.context?.page_name,
        page_url: properties.context?.page_url,
      }
    case 'app_push_banner_viewed':
      return {
        event_type: 'impression',
        event_name: 'App Install Banner Viewed',
        bucket: properties.context?.bucket,
        locale: properties.context?.locale,
      }

    case 'sort_opened':
      return {
        event_type: 'click',
        event_name: 'Sort Opened',
      }

    case 'sort_used': {
      let eventName = 'Sort Used'
      if (properties.entity_id === 'reviews') {
        eventName = 'Review Sort Used'
      }
      return {
        event_type: 'click',
        event_name: eventName,
        sort_used: properties.context?.sort_used,
        ...properties?.context?.productProperties,
      }
    }

    case 'faq_viewed':
      return {
        event_type: 'impression',
        event_name: 'FAQ Viewed',
        no_of_FAQ_cards: properties.context?.no_of_FAQ_cards,
      }

    case 'faq_clicked':
      return {
        event_type: 'click',
        event_name: 'FAQ Clicked',
        FAQ_description: properties.context?.FAQ_description,
        ...properties?.context?.productProperties,
      }

    case 'address_addition_initiated':
      return {
        event_type: 'click',
        event_name: 'Address Addition Initiated',
        default_address_present: properties.context?.default_address_present,
        no_of_address: properties.context?.no_of_address,
      }

    case 'address_addition_successful':
      return {
        event_type: 'click',
        event_name: 'Address Addition Successful',
        default_address_present: properties.context?.default_address_present,
        no_of_address: properties.context?.no_of_address,
      }

    case 'address_edit_successful':
      return {
        event_type: 'click',
        event_name: 'Edit Address Successful',
        is_default_address: properties.context?.is_default_address,
        no_of_address: properties.context?.no_of_address,
      }

    case 'product_gallery_viewed':
      return {
        event_type: 'impression',
        event_name: 'Product Gallery Viewed',
        sku: properties.context?.sku,
        product_id: properties.context?.product_id,
        variant_id: properties.context?.variant_id,
      }

    case 'reviews_sorted':
      return {
        event_type: 'click',
        event_name: 'Reviews Sorted',
        sku: properties.context?.sku,
        product_id: properties.context?.product_id,
        variant_id: properties.context?.variant_id,
        has_photo_filter: properties.context?.has_photo_filter,
        sort_type: properties.context?.sort_type,
        ...properties?.context?.productProperties,
      }

    case 'review_page_navigated':
      return {
        event_type: 'click',
        event_name: 'Review Page Navigated',
        page_number: properties.context?.page_number,
        navigation_type: properties.context?.navigation_type,
        ...properties?.context?.productProperties,
      }

    case 'review_viewed':
      return {
        event_type: 'impression',
        event_name: 'Review Viewed',
        review_count: properties.context?.review_count,
        page_number: properties.context?.page_number,
        ...properties?.context?.productProperties,
      }

    case 'search_completed':
      return {
        event_type: 'search',
        event_name: 'Search Completed',
        null_search: properties.context?.null_search,
        result_count: properties.context?.result_count,
        products: properties.context?.products,
        oos_product_count: properties.context?.oos_product_count,
        ...properties?.context.productProperties,
      }

    case 'similar_collection_clicked':
      return {
        event_type: 'click',
        event_name: 'Similar Collection Clicked',
        filter_available: properties.context?.filter_available,
        filter_count: properties.context?.filter_count,
        filter_used: properties.context?.filter_used,
      }

    case 'subcollection_filter_used':
      return {
        event_type: 'click',
        event_name: 'Subcollection Filter Used',
        filter_used: properties.context?.filter_used,
        filter_available: properties.context?.filter_available,
      }

    case 'cart_page_action':
      return {
        event_type: 'cart_page_action',
        event_name: 'cart_page_action',
        cart_id: (properties.context as { cartID?: string })?.cartID,
        entity_type: properties.entity_type,
        product_deets: properties.context?.product_deets,
        from: properties.context?.from,
        to: properties.context?.to,
        to_saved_for_later: properties.context?.to_saved_for_later,
      }

    case 'cart_items_fetched':
      return {
        event_type: 'cart_items_fetched',
        event_name: 'cart_items_fetched',
        cart_id: (properties.context as { cartID?: string })?.cartID,
        list_items: properties.context?.list_items,
      }

    case 'update_item_quantity':
      if (properties.context?.product_quantity_changed_cart != null) {
        return {
          event_type: 'update_item_quantity',
          event_name: 'Updated Item Quantity',
          cart_id: (properties.context as { cartID?: string })?.cartID,
          line_item_id: properties.entity_id,
          product_quantity_changed_cart: properties.context.product_quantity_changed_cart,
          ...(properties.context?.movement_details ? { movement_details: properties.context.movement_details } : {}),
          ...(properties.context?.variant_id ? { variant_id: properties.context.variant_id } : {}),
          ...(properties.context?.product_id != null && properties.context?.product_id !== ''
            ? { product_id: String(properties.context.product_id) }
            : {}),
          ...(properties.context?.group_id ? { group_id: properties.context.group_id } : {}),
          ...(properties.context?.batch_update_details
            ? { batch_update_details: properties.context.batch_update_details }
            : {}),
        }
      }
      return {
        event_type: 'click',
        event_name: 'Updated Item Quantity',
        cart_id: (properties.context as { cartID?: string })?.cartID,
        variant_id: properties.entity_id,
        movement_details: properties.context?.movement_details,
        is_modal: properties.context?.is_modal ?? false,
      }

    case 'save_for_later':
      return {
        event_type: 'click',
        event_name: 'Saved for Later',
        cart_id: (properties.context as { cartID?: string })?.cartID,
        variant_id: properties.entity_id,
        quantity: properties.context?.quantity,
        is_modal: properties.context?.is_modal ?? false,
      }

    case 'move_to_bag':
      return {
        event_type: 'click',
        event_name: 'Moved to Bag',
        cart_id: (properties.context as { cartID?: string })?.cartID,
        variant_id: properties.entity_id,
        quantity: properties.context?.quantity,
        is_modal: properties.context?.is_modal ?? false,
      }

    case 'remove_from_cart': {
      return {
        event_type: 'click',
        event_name: 'Removed from Cart',
        cart_id: (properties.context as { cartID?: string })?.cartID,
        variant_id: properties.entity_id,
        ...(properties.context?.quantity !== undefined && { quantity: properties.context?.quantity }),
        is_modal: properties.context?.is_modal ?? false,
        productProperties: properties?.context.productProperties,
      }
    }

    case 'subcollection_card_click': {
      return {
        event_type: 'click',
        event_name: 'Subcategory Filter Used',
        filter_used: properties.context?.subcollection_title,
      }
    }

    case 'variant_selected':
      return {
        event_type: 'click',
        event_name: 'Variant Selected',
        product_id: properties.context?.product_id ? properties.context.product_id.toString() : undefined,
        price: properties.context?.price,
        color: properties.context?.color,
        size: properties.context?.size,
        sku: properties.context?.sku,
        variant_id: properties.context?.variant_id ? properties.context.variant_id.toString() : undefined,
        department: properties.context?.department,
        subdepartment: properties.context?.subdepartment,
        division: properties.context?.division,
        age: properties.context?.age,
        is_limited_edition_color: properties.context?.is_limited_edition_color,
        color_swatch_position: properties?.context?.color_swatch_position,
        ...properties?.context?.productProperties,
        oos_flag: properties.context?.oos_flag,
        is_preorder: properties.context?.productProperties?.is_preorder ?? properties.context?.is_preorder,
        preorder_edd: properties.context?.productProperties?.preorder_edd,
        is_waitlist: properties.context?.is_waitlist,
        autoselect: !!properties.context?.autoselect,
        has_video: properties.has_video ?? properties.context?.has_video,
        video_id: properties.video_id ?? properties.context?.video_id,
        is_final_sale: properties.context?.is_final_sale,
        is_app_exclusive: properties.context?.is_app_exclusive,
      }

    case 'form_open': {
      if (properties.entity_id === 'attentive_popup_opened') {
        const eventType = 'impression'

        const eventName = 'Signup Popup Viewed'
        return {
          event_type: eventType,
          event_name: eventName,
          creative_id: properties.context?.creative_id,
        }
      }
      if (properties.entity_id === 'review_form_impression') {
        return {
          event_type: 'impression',
          event_name: 'Review Form Viewed',
          product_id: properties.context?.productId,
          spid: properties.context?.spid,
          order_id: properties.context?.orderId,
          lb_user_id: properties.context?.lb_user_id,
          source: properties.context?.source,
        }
      }
      return null
    }

    case 'form_submit':
      if (properties.entity_id === 'submit_review_click') {
        return {
          event_type: 'review',
          event_name: 'Submit Review Clicked',
          product_id: properties.context?.productId,
          spid: properties.context?.spid,
          order_id: properties.context?.orderId,
          lb_user_id: properties.context?.lb_user_id,
          source: properties.context?.source,
        }
      }
      if (properties.entity_id === 'successful_review_submission') {
        return {
          event_type: 'review',
          event_name: 'Review Successfully Submitted',
          product_id: properties.context?.productId,
          spid: properties.context?.spid,
          order_id: properties.context?.orderId,
          lb_user_id: properties.context?.lb_user_id,
          source: properties.context?.source,
          stamped_response: properties.context?.stampedResponse,
        }
      }
      return {
        event_type: properties.context?.action == 'account_login' ? 'login' : 'signup',
        event_name: properties.context?.action == 'account_login' ? 'User Logged In' : 'User Signed Up',
        signup_method: properties.context?.signup_method,
        login_method: properties.context?.login_method,
      }

    case 'product_gallery_scrolled':
      return {
        event_type: 'impression',
        event_name: 'Product Gallery Scrolled',
        name: properties.context?.name,
        department: properties.context?.department,
        division: properties.context?.division,
        subdepartment: properties.context?.subdepartment,
        product_id: properties.context?.product_id ? properties.context.product_id.toString() : undefined,
        ...properties?.context?.productProperties,
      }

    case 'send_referral_email_invite': {
      return {
        event_type: 'referral',
        event_name: 'Referral Initiated',
      }
    }
    case 'product_card_scrolled':
      return {
        event_type: 'impression',
        event_name: 'Product Card Scrolled',
        name: properties?.context?.name,
        department: properties?.context?.department,
        division: properties?.context?.division,
        subdepartment: properties?.context?.subdepartment,
        product_id: properties?.context?.product_id ? properties?.context?.product_id.toString() : undefined,
        ...properties?.context?.productProperties,
      }

    case 'return_init_started': {
      return {
        event_type: 'return_init_started',
        event_name: 'Return Initialization Started',
        order_id: properties.context?.order_id,
      }
    }

    case 'return_init_completed': {
      return {
        event_type: 'return_init_completed',
        event_name: 'Return Initialization Completed',
        order_id: properties.context?.order_id,
        duration_ms: properties.context?.duration_ms,
        returnable_line_items_count: properties.context?.returnable_line_items_count,
      }
    }

    case 'return_init_stuck': {
      return {
        event_type: 'return_init_stuck',
        event_name: 'Return Initialization Stuck',
        order_id: properties.context?.order_id,
        stage: properties.context?.stage,
        elapsed_ms: properties.context?.elapsed_ms,
        threshold_ms: properties.context?.threshold_ms,
      }
    }

    case 'return_flow_api_error': {
      return {
        event_type: 'return_flow_api_error',
        event_name: 'Return Flow API Error',
        order_id: properties.context?.order_id,
        api: properties.context?.api,
        stage: properties.context?.stage,
        http_status: properties.context?.http_status,
        error_code: properties.context?.error_code,
        error_message: properties.context?.error_message,
        is_network_error: properties.context?.is_network_error,
        duration_ms: properties.context?.duration_ms,
      }
    }

    case 'return_line_items_screen_viewed': {
      return {
        event_type: 'return_line_items_screen_viewed',
        event_name: 'Return Line Items Screen Viewed',
        variant_count: properties.context?.variant_count,
        total_quantity: properties.context?.total_quantity,
      }
    }
    case 'return_fee_tooltip_hover': {
      return {
        event_type: 'return_fee_tooltip_hovered',
        event_name: 'Return Fee Tooltip Hovered',
      }
    }
    case 'submit_line_items_cta_clicked': {
      return {
        event_type: 'submit_line_items_cta_clicked',
        event_name: 'Return Line Items Selected',
      }
    }
    case 'returns_refund_type_submitted': {
      return {
        event_type: 'returns_refund_type_submitted',
        event_name: 'Return Refund Type Submitted',
      }
    }
    case 'refund_amount_screen_viewed': {
      return {
        event_type: 'refund_amount_screen_viewed',
        event_name: 'Return Summary Page Viewed',
      }
    }
    case 'return_confirm_cta_clicked': {
      return {
        event_type: 'return_confirm_cta_clicked',
        event_name: 'Return Confirm CTA Clicked',
      }
    }
    case 'return_confirm_screen_viewed': {
      return {
        event_type: 'return_confirm_screen_viewed',
        event_name: 'Return Confirmed Page Viewed',
      }
    }
    case 'return_item_selected': {
      return {
        event_type: 'return_item_selected',
        event_name: 'Return Item Selected',
      }
    }
    case 'exchange_for_new_selected': {
      return {
        event_type: 'exchange_for_new_selected',
        event_name: 'Exchange for New Selected',
      }
    }
    case 'progressive_return_flow_continue_cta': {
      return {
        event_type: 'progressive_return_flow_continue_cta',
        event_name: 'Progressive Return Flow Continue CTA',
        entity_type: properties.entity_type,
        entity_id: properties.entity_id,
        context: {
          ...((properties.context as Record<string, unknown> | undefined) || {}),
          ...((properties.failure_reason as Record<string, string> | undefined) && {
            failure_reason: properties.failure_reason as Record<string, string>,
          }),
        },
      }
    }
    case 'progressive_return_flow_visible': {
      return {
        event_type: 'impression',
        event_name: 'Progressive Return Flow Visible',
        entity_type: properties.entity_type,
        entity_id: properties.entity_id,
        context: {
          ...(properties.context || {}),
          section: {
            ...(properties.context?.reason && { reason: properties.context?.reason }),
            ...(properties.context?.sub_reason && { sub_reason: properties.context?.sub_reason }),
            ...(properties.context?.details && { details: properties.context?.details }),
            ...(properties.context?.evidence && { evidence: properties.context?.evidence }),
          },
        },
      }
    }

    case 'return_less_refund_message_visible': {
      return {
        event_type: 'return_less_refund_message_visible',
        event_name: 'Return Less Refund Message Visible',
        entity_type: properties.entity_type,
        entity_id: properties.entity_id,
      }
    }
    case 'start_return_cta_click': {
      const context = properties.context as { page_name?: string; order_id?: number; order_number?: string } | undefined
      return {
        event_type: 'click',
        event_name: 'Start Return CTA Clicked',
        page_name: context?.page_name,
        order_id: context?.order_id,
        order_number: context?.order_number,
      }
    }
    case 'cart_page_view':
      return {
        event_type: 'page_load',
        event_name: 'Cart Viewed',
        cart_id: (properties.context as { cartID?: string })?.cartID,
        is_modal: properties.context?.is_modal ?? false,
      }

    case 'filter_opened':
      return {
        event_type: 'click',
        event_name: 'Filter Opened',
        filter_key: (properties.context as { filter_key?: string })?.filter_key ?? '',
      }

    case 'filter_applied': {
      let eventName = 'Filter Applied'
      if (properties.entity_id === 'reviews') {
        eventName = 'Review Filter Applied'
      }
      return {
        event_type: 'click',
        event_name: eventName,
        filter_key: (properties.context as { filter_key?: string })?.filter_key ?? '',
        filter_value: (properties.context as { filter_value?: string })?.filter_value ?? '',
      }
    }

    case 'filters_cleared':
      return {
        event_type: 'click',
        event_name: 'Filters Cleared',
      }

    case 'individual_filters_cleared':
      return {
        event_type: 'click',
        event_name: 'Individual Filters Cleared',
        filter_key: (properties.context as { filter_key?: string })?.filter_key ?? '',
        filter_value: (properties.context as { filter_value?: string })?.filter_value ?? '',
      }

    case 'empty_filtered_results':
      return {
        event_type: 'empty_filtered_results',
        event_name: 'Empty Filtered Results',
      }

    case 'support_chat_initiated':
      return {
        event_type: 'support_chat_initiated',
        event_name: 'Support Chat Initiated',
      }
    case 'support_section_clicked':
      return {
        event_type: 'support_section_clicked',
        event_name: 'Support Section Clicked',
        section_type: properties.context?.section_type,
      }

    // B2B Authentication Funnel Events
    case TELEMETRY_EVENT_NAMES.B2B_PAGE_VIEW:
      return {
        event_type: properties.event_type,
        event_name: 'B2B Login Page Viewed',
        page_name: properties.page_name,
      }

    case TELEMETRY_EVENT_NAMES.B2B_LOGIN_EMAIL_SUBMITTED:
      return {
        event_type: properties.event_type,
        event_name: 'B2B Login Email Submitted',
      }

    case TELEMETRY_EVENT_NAMES.B2B_LOGIN_OTP_SUBMITTED:
      return {
        event_type: properties.event_type,
        event_name: 'B2B Login OTP Submitted',
      }

    case TELEMETRY_EVENT_NAMES.B2B_LOGIN_SUCCESS:
      return {
        event_type: properties.event_type,
        event_name: 'B2B Login Successful',
        user_id: properties.user_id,
      }

    case TELEMETRY_EVENT_NAMES.B2B_LOGIN_FAILED:
      return {
        event_type: properties.event_type,
        event_name: 'B2B Login Failed',
        failure_reason: properties.failure_reason,
      }

    case TELEMETRY_EVENT_NAMES.B2B_DELETE_CUSTOMIZATION_FROM_CART:
      return {
        event_type: properties.event_type,
        event_name: 'Removed Customization From Cart',
        group_id: properties.entity_id,
      }

    case TELEMETRY_EVENT_NAMES.B2B_DELETE_CART_GROUP:
      return {
        event_type: properties.event_type,
        event_name: 'Deleted Cart Group',
        group_id: properties.context?.group_id ?? properties.entity_id,
        ...(properties.context?.variant_id ? { variant_id: properties.context.variant_id } : {}),
        ...(properties.context?.product_id != null && properties.context?.product_id !== ''
          ? { product_id: String(properties.context.product_id) }
          : {}),
      }

    case 'product_full_details_viewed':
      return {
        event_type: 'click',
        event_name: 'Product Full Details Viewed',
        ...properties?.context?.productProperties,
      }

    case 'atc_link_clicked':
      return {
        event_type: 'click',
        event_name: 'ATC Link Clicked',
        ...properties?.context?.productProperties,
      }

    case 'ctl_click': {
      return {
        event_type: 'click',
        event_name: 'CTL Stylitics Modal Viewed',
        nav_element:
          properties.entity_id === 'click_menu_link' ? properties.context?.menuTitle : properties.context?.nav_element,
        top_nav_element: properties.entity_id === 'click_menu_link' ? properties.context?.menuTitle : undefined,
        element_name: 'Single Swimlane',
        product_id: properties.context?.product_id ? properties.context.product_id.toString() : undefined,
        price: properties.context?.product_price ? Number(properties.context.product_price) : undefined,
        element_type: properties.context?.contentContainerType,
        element_display_name:
          properties.context?.contentContainerDisplayName || properties.context?.element_display_name,
        element_id: properties.context?.trackingId || properties.context?.id || properties.context?.element_id,
        product_price: properties.context?.product_price,
        product_count: productIdsArr.length,
        cart_value: properties.context?.isCompanyFunded
          ? undefined
          : properties.context?.totalPrice
            ? Number(properties.context.totalPrice)
            : undefined,
        variants: formattedLineItems,
        name: properties.context?.name,
        color: properties.context?.color_name,
        color_variant: properties.context?.color_variant,
        rating: properties.context?.rating,
        custom_variants: properties.context?.custom_variants,
        oos_flag: properties.context?.oos_flag,
        gender: properties.context?.gender,
        creativeId: properties.context?.creativeId,
        tabData: getRecommendationProperties(properties?.context?.tabData),
        ...properties?.context.productProperties,
        L1: properties?.context?.L1,
        L2: properties?.context?.L2,
        L3: properties?.context?.L3,
        ...getB2BProperties(properties.context?.isB2BFlow, properties.context?.isCompanyFunded),
      }
    }
    case 'dob_modal_opened':
      return {
        event_type: 'click',
        event_name: 'Dob Modal Opened',
        status: properties.status,
      }
    case 'dob_added_error':
      return {
        event_type: 'click',
        event_name: 'Dob Modal Error',
        status: properties.status,
      }
    case 'dob_added':
      return {
        event_type: 'click',
        event_name: 'Dob Modal Success',
        status: properties.status,
      }
    case 'dob_removed':
      return {
        event_type: 'click',
        event_name: 'Dob Removed',
        status: properties.status,
      }

    case 'subscriptions_list_viewed':
      return {
        event_type: 'subscriptions_list_viewed',
        event_name: 'Subscriptions List Viewed',
        list_items: properties.context?.list_items,
      }
    case 'subscriptions_list_error':
      return {
        event_type: 'subscriptions_list_error',
        event_name: 'Subscriptions List Error',
      }
    case 'subscription_page_action':
      return {
        event_type: properties.event_type,
        event_name: 'Subscriptions Page Action',
        entity_type: properties.entity_type,
        entity_id: properties.entity_id,
        product_id: properties.context?.product_id,
        variant_id: properties.context?.variant_id,
        quantity: properties.context?.quantity,
        price: properties.context?.price,
        subscriptions_id: properties.context?.subscriptions_id,
        subscription_frequency: properties.context?.subscription_frequency,
        next_order_date: properties.context?.next_order_date,
        last_purchase_date: properties.context?.last_purchase_date,
      }
    case 'subscription_management_actions':
      return {
        event_type: properties.event_type,
        event_name: 'Subscriptions Management Actions',
        entity_type: properties.entity_type,
        entity_id: properties.entity_id,
        from: properties.context?.from,
        to: properties.context?.to,
        variant_id: properties.context?.variant_id,
        product_id: properties.context?.product_id,
        quantity: properties.context?.quantity,
        price: properties.context?.price,
        subscription_id: properties.context?.subscription_id,
        flow_back_amount: properties.context?.flow_back_amount,
        cancellation_reason: properties.context?.cancellation_reason,
        cancellation_comment: properties.context?.cancellation_comment,
      }
    case 'change_shipping_address_error':
      return {
        event_type: properties.event_type,
        event_name: 'Change Shipping Address Error',
        entity_type: properties.entity_type,
        entity_id: properties.entity_id,
        error: properties.context?.error,
        subscription_id: properties.context?.subscription_id,
      }

    // order details actions
    case 'order_details_actions':
      return {
        event_type: properties.event_type,
        event_name: 'Order Details Actions',
        entity_type: properties.entity_type,
        variant_id: properties.context?.variant_id,
        product_id: properties.context?.product_id,
        quantity: properties.context?.quantity,
        price: properties.context?.price,
        order_id: properties.context?.order_id,
        subscription_id: properties.context?.subscription_id,
        line_items: properties.context?.line_items,
      }

    // subscription vault payment events
    case 'payment_change_initilised':
      return {
        event_type: 'payment_change',
        event_name: 'Payment Change Initiated',
        via_update_card_button: properties.via_update_card_button,
        from_payment_method: properties.from_payment_method,
        from_payment_gateway: properties.from_payment_gateway,
      }

    case 'payment_change_status':
      return {
        event_type: 'payment_change',
        event_name: properties.entity_id === 'success' ? 'Payment Change Success' : 'Payment Change Failed',
        from_payment_method: properties.from_payment_method,
        to_payment_method: properties.to_payment_method,
        from_payment_gateway: properties.from_payment_gateway,
        to_payment_gateway: properties.to_payment_gateway,
        reason_for_failure: properties.reason_for_failure_if_failed,
      }

    case TELEMETRY_EVENT_NAMES.APP_EXCLUSIVE_MODAL_OPENED:
      return {
        event_type: 'click',
        event_name: 'App Exclusive Modal Opened',
        is_final_sale: properties.context?.is_final_sale,
      }
    case TELEMETRY_EVENT_NAMES.APP_EXCLUSIVE_LINK_CLICKED:
      return {
        event_type: 'click',
        event_name: 'App Exclusive Link Clicked',
        is_final_sale: properties.context?.is_final_sale,
      }

    case TELEMETRY_EVENT_NAMES.LIVE_EVENT_CTA_CLICKED:
      return {
        event_type: 'click',
        event_name: 'Live Event CTA Clicked',
        cta_type: properties.context?.ctaType,
        state: properties.context?.state,
        platform: properties.context?.platform,
        slug: properties.context?.slug,
      }

    case TELEMETRY_EVENT_NAMES.ORDER_SEARCH_STARTED:
      return {
        event_type: TELEMETRY_EVENT_NAMES.ORDER_SEARCH_STARTED,
        event_name: 'Order Search Started',
        formData: properties?.context?.formData,
      }
    case TELEMETRY_EVENT_NAMES.ORDER_SEARCH_RESULTS:
      return {
        event_type: TELEMETRY_EVENT_NAMES.ORDER_SEARCH_RESULTS,
        event_name: 'Order Search Results',
        formData: properties?.context?.formData,
      }
    case TELEMETRY_EVENT_NAMES.FIND_MY_ORDER_WIDGET_CLICKED:
      return {
        event_type: TELEMETRY_EVENT_NAMES.FIND_MY_ORDER_WIDGET_CLICKED,
        event_name: 'Find My Order Widget Clicked',
        formData: properties?.context?.formData,
      }
    case TELEMETRY_EVENT_NAMES.INITIATE_OTP_CHECK:
      return {
        event_type: TELEMETRY_EVENT_NAMES.INITIATE_OTP_CHECK,
        event_name: 'Initiate OTP Check',
        properties: properties?.context?.properties,
      }
    case TELEMETRY_EVENT_NAMES.OTP_RESEND_ATTEMPT:
      return {
        event_type: TELEMETRY_EVENT_NAMES.OTP_RESEND_ATTEMPT,
        event_name: 'OTP Resend Attempt',
        properties: properties?.context?.properties,
      }
    case TELEMETRY_EVENT_NAMES.OTP_VALIDATE_ATTEMPT:
      return {
        event_type: TELEMETRY_EVENT_NAMES.OTP_VALIDATE_ATTEMPT,
        event_name: 'OTP Validate Attempt',
        properties: properties?.context?.properties,
      }
    case TELEMETRY_EVENT_NAMES.SIGN_IN_STARTED:
      return {
        event_type: TELEMETRY_EVENT_NAMES.SIGN_IN_STARTED,
        event_name: 'Sign In Started',
        properties: properties?.context?.properties,
      }
    case TELEMETRY_EVENT_NAMES.FMO_SWITCH_ACCOUNT_CTA_CLICKED:
      return {
        event_type: TELEMETRY_EVENT_NAMES.FMO_SWITCH_ACCOUNT_CTA_CLICKED,
        event_name: 'FMO Switch Account CTA Clicked',
        properties: properties?.context?.properties,
      }
    case TELEMETRY_EVENT_NAMES.LOGOUT:
      return {
        event_type: TELEMETRY_EVENT_NAMES.LOGOUT,
        event_name: 'Log out',
        properties: properties?.context,
      }
    case 'subscriptions_shipping_frequency_change':
      return {
        event_type: 'subscriptions_shipping_frequency_change',
        event_name: 'Subscriptions Shipping Frequency Change',
        ...(properties?.context || {}),
      }
    case TELEMETRY_EVENT_NAMES.SIZE_CHART_CLICKED:
      return {
        event_type: TELEMETRY_EVENT_NAMES.SIZE_CHART_CLICKED,
        event_name: 'Size Chart Clicked',
        properties: properties.context,
      }
    case TELEMETRY_EVENT_NAMES.SIZE_CHART_TAB:
      return {
        event_type: TELEMETRY_EVENT_NAMES.SIZE_CHART_TAB,
        event_name: 'Size Chart Tab Clicked',
        properties: properties.context,
      }
    case TELEMETRY_EVENT_NAMES.SIZE_CHART_TOGGLE:
      return {
        event_type: TELEMETRY_EVENT_NAMES.SIZE_CHART_TOGGLE,
        event_name: 'Size Chart Unit Toggle Clicked',
        properties: properties.context,
      }
    case 'subscriptions_radio_button_selected':
      return {
        event_type: 'subscriptions_radio_button_selected',
        event_name: 'Subscriptions Radio Button Selected',
        ...(properties?.context || {}),
      }

    case TELEMETRY_EVENT_NAMES.CUSTOMIZATION_CHECKBOX_CLICK:
      return {
        event_type: TELEMETRY_EVENT_NAMES.CUSTOMIZATION_CHECKBOX_CLICK,
        event_name: 'Customization Checkbox Clicked',
        is_selected: properties?.context?.is_selected,
        product_id: properties?.context?.product_id,
      }
    case TELEMETRY_EVENT_NAMES.CUSTOMIZATION_PAGE_VIEW:
      return {
        event_type: TELEMETRY_EVENT_NAMES.CUSTOMIZATION_PAGE_VIEW,
        event_name: 'Customization Page Viewed',
        mode: properties?.context?.mode,
        product_id: properties?.context?.product_id,
        variant_quantity_map: properties?.context?.variant_quantity_map,
      }
    case TELEMETRY_EVENT_NAMES.CUSTOMIZATION_PAGE_LOGO_UPLOAD:
      return {
        event_type: TELEMETRY_EVENT_NAMES.CUSTOMIZATION_PAGE_LOGO_UPLOAD,
        event_name: 'Customization Page Logo Uploaded',
        logoUploaded: properties?.context?.logoUploaded,
      }
    case TELEMETRY_EVENT_NAMES.CUSTOMIZATION_PAGE_NEXT_BUTTON:
      return {
        event_type: TELEMETRY_EVENT_NAMES.CUSTOMIZATION_PAGE_NEXT_BUTTON,
        event_name: 'Customization Page Next Button Clicked',
        nextSelected: properties?.context?.nextSelected,
      }
    case TELEMETRY_EVENT_NAMES.REVIEW_AUTO_SCROLL_TRIGGERED:
      return {
        event_type: TELEMETRY_EVENT_NAMES.REVIEW_AUTO_SCROLL_TRIGGERED,
        event_name: 'Auto Scroll Triggered',
        ...(properties?.context || {}),
      }

    case TELEMETRY_EVENT_NAMES.ADD_PHOTO_CLICKED:
      return {
        event_type: 'click',
        event_name: 'Add Photos Clicked',
        ...(properties?.context || {}),
        product_id: properties?.context?.product_id,
      }

    case TELEMETRY_EVENT_NAMES.REVIEW_IMAGE_UPLOADED:
      return {
        event_type: 'action',
        event_name: 'Review Image Uploaded',
        ...(properties?.context || {}),
        product_id: properties?.context?.product_id,
      }

    case TELEMETRY_EVENT_NAMES.RATING_ADDED:
      return {
        event_type: 'review',
        event_name: 'Rating Added',
        product_id: properties?.context?.product_id,
        name: properties?.context?.name,
        rating: properties?.context?.rating,
      }

    case TELEMETRY_EVENT_NAMES.REVIEW_DETAILS_ADDED:
      return {
        event_type: 'review',
        event_name: 'Review Details Added',
        step_number: properties?.context?.step_number,
        sectionname: properties?.context?.sectionname,
        page_name: properties?.context?.page_name,
        product_id: properties?.context?.product_id,
        name: properties?.context?.name,
      }

    case TELEMETRY_EVENT_NAMES.REVIEW_DETAILS_SUBMITTED:
      return {
        event_type: 'review',
        event_name: 'Review Details Submitted',
        step_number: properties?.context?.step_number,
        page_name: properties?.context?.page_name,
        product_id: properties?.context?.product_id,
        name: properties?.context?.name,
      }

    case TELEMETRY_EVENT_NAMES.REVIEW_SUBMISSION_ERROR_LOG:
      return {
        event_type: 'review',
        event_name: 'Review Submission Error log',
        error_type: properties?.context?.error_type,
        error_message: properties?.context?.error_message,
        product_id: properties?.context?.product_id,
        step_number: properties?.context?.step_number,
      }
    case 'checkout_action': {
      const ctx = (properties.context as Record<string, unknown> | undefined) ?? {}
      if (ctx.checkout_pay_button_click === true) {
        return {
          event_type: 'checkout_action',
          event_name: 'Checkout Pay Button Clicked',
          entity_type: properties.entity_type,
          entity_id: properties.entity_id,
          flow: ctx.flow,
          payment_method: ctx.payment_method,
          payment_init_token: ctx.payment_init_token,
          checkout_pay_button_click: true,
          error_reason: ctx.error_reason,
        }
      }
      return null
    }

    case 'payment_initiated_web_checkout': {
      const ctx = (properties.context as Record<string, unknown> | undefined) ?? {}
      return {
        event_type: 'payment_initiated_web_checkout',
        event_name: 'Payment Initiated',
        entity_type: properties.entity_type,
        entity_id: properties.entity_id,
        flow: ctx.flow,
        payment_method: ctx.payment_method,
        payment_gateway: ctx.payment_gateway,
        payment_init_token: ctx.payment_init_token,
      }
    }

    case 'payment_authorised': {
      const ctx = (properties.context as Record<string, unknown> | undefined) ?? {}
      return {
        event_type: 'payment_authorised',
        event_name: 'Payment Authorised',
        entity_type: properties.entity_type,
        entity_id: properties.entity_id,
        flow: ctx.flow,
        payment_method: ctx.payment_method,
        payment_gateway: ctx.payment_gateway,
        payment_init_token: ctx.payment_init_token,
      }
    }

    case 'checkout_error_log_web_checkout': {
      const ctx = (properties.context as Record<string, unknown> | undefined) ?? {}
      return {
        event_type: 'checkout_error_log_web_checkout',
        event_name: 'Checkout Error Log',
        entity_type: properties.entity_type,
        entity_id: properties.entity_id,
        flow: ctx.flow,
        payment_method: ctx.payment_method,
        payment_gateway: ctx.payment_gateway,
        payment_init_token: ctx.payment_init_token,
        error_reason: ctx.error_reason,
        error_type: ctx.error_type,
        error_stage: ctx.error_stage,
        error_origin: ctx.error_origin,
        error_message: ctx.error_message,
        http_status: ctx.http_status,
        error_code: ctx.error_code,
        api_endpoint: ctx.api_endpoint,
      }
    }

    case 'atc_confirmation_bottom_sheet_view': {
      const ctx = (properties.context as Record<string, unknown> | undefined) ?? {}
      return {
        event_type: 'impression',
        event_name: 'ATC Confirmation Bottom Sheet Viewed',
        variant: ctx.variant,
        recommendation_count: ctx.recommendationCount,
      }
    }

    case 'atc_confirmation_bottom_sheet_dismiss':
      return {
        event_type: 'click',
        event_name: 'ATC Confirmation Bottom Sheet Dismissed',
      }

    default:
      return null
  }
}

export const isBotAgent = () => {
  if (navigator?.userAgent) {
    const ua = navigator.userAgent

    const botUaNames = ['facebookexternalhit', 'facebookcatalog', 'Googlebot', 'remotasks', 'adsbot']
    let isBot = false
    botUaNames.forEach(nm => {
      if (ua.includes(nm)) {
        isBot = true
      }
    })
    return isBot
  }
  return false
}
